import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button, Checkbox, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import MosSmartDialog from '../MosSmartDialog'
import createDialogTitle from '../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import { previewBatchStart } from './batchApi'
import BatchReasonsList from './batchReasons'
import { BATCH_TABLE_CONTAINER_STYLE } from './batchTable'

/**
 * Historia 120 — Bloque 11 (Jupiter, paquete P5), `features/S116-previsualizacion-y-modo-jupiter.feature`
 * (S116, S117, S119) and `features/S206-preparacion-y-reporte-jupiter.feature` (S206) — reshaped by
 * the **ENMIENDA 06** (G3-10), `features/S233-ui-previsualizacion-y-barra-de-lote-jupiter.feature`
 * (S233, S234, S235, S236).
 *
 * 🔒 D-I10: THE ONE preview screen, shared verbatim by the three selection paths (explicit jobs,
 * planning-group shortcut, batch resume) -- the caller only ever varies `request.selectionType` (and
 * the matching `jobs`/`planningGroup`+`originJob`/`batchId`); this component never branches on it.
 * There is no second screen and no second rule for the other two vias -- whatever P6 wires for the
 * group shortcut and the batch resume mounts this SAME component.
 *
 * Consumes N1 (`previewBatchStart`, `batchApi.js`) exactly once per gesture: the fetch fires on the
 * `open:false -> true` transition and NOT again while `open` stays true, so a caller that stacks
 * `BatchStartModeDialog` on top of this one WITHOUT closing it (S123's "vuelve al punto anterior sin
 * perder su selección") never re-fetches and never resets the operator's toggles.
 *
 * 🔴 ENMIENDA 06 -- EL HALLAZGO DEL PO, CON EL PRODUCTO DELANTE: la lista plana no escalaba (*"hay que
 * tener en cuenta cuando hay muchos jobs"*). Cambia la FORMA, no el comportamiento:
 *   · **S116** -- las cuatro clasificaciones de RN14 siguen siendo SECCIONES (no pestañas, no una tabla
 *     única con columna Estado), cada epígrafe con su RECUENTO anexo, y las filas de cada una en una
 *     TABLA compacta: casilla · orden · referencia · job · grupo · fecha planificada.
 *   · **S233** -- la altura máxima y el scroll viven en la TABLA de cada sección, no en el diálogo: con
 *     cuarenta filas el diálogo mide lo mismo que con cuatro, los epígrafes siguen visibles y el botón
 *     de confirmar no se va de la pantalla.
 *   · **S234** -- buscador desde QUINCE filas que filtra SOBRE LA RESPUESTA YA RECIBIDA. 🚫 Nunca vuelve
 *     a llamar a N1 (RN8.9): una llamada por pulsación caería sobre un gesto que no escribe nada (INV9)
 *     y con ~7 lecturas por job candidato detrás.
 *   · **S235** -- el recuento del epígrafe es el TOTAL de su sección y el filtro NO lo altera; lo que se
 *     confirma sigue siendo la lista explícita que el operario marcó (RN18/POL4), incluida la que el
 *     filtro esconde. El BOTÓN sigue SIN recuento (S119 intacto).
 *   · **S236** -- grupo y fecha planificada se pintan con `planningGroup`/`plannedStartDate`, que la fila
 *     de N1 YA publica (G1-2/F1): cero peticiones nuevas, cero campos nuevos (§8.4).
 * 🔒 `generic/SmartMateriaUITable.jsx` NO se toca (S114): esta tabla es MUI a secas, propia del diálogo.
 *
 * S117/D-I7 -- each `NEEDS_DECISION` row carries its OWN Include/Exclude switch, starting EXCLUDED;
 * only the ones actually switched to Include join the confirmed list. Which OTHER device it is
 * active on is shown per row (`activeDevices[].name`); together with the translated reason
 * (`ActiveOnAnotherDevice`, via the one shared reason renderer, `batchReasons.jsx`) this is what
 * tells the operator including it means the job stays active on two machines -- there is no
 * dedicated i18n key for that extra sentence (none exists in the frozen catalogue), so the warning is
 * this combination of data, not a bespoke string. La enmienda 06 lo mueve a la FILA sin cambiarle la
 * semántica: sigue siendo la decisión por job y el valor de ausencia sigue siendo excluido.
 *
 * S119 -- when nothing is checked/included (starting from a preview with zero `STARTABLE` rows and
 * no row ever switched to Include), the confirm button is unavailable and the
 * `No job can be started` notice is shown instead.
 *
 * S206/G3-1/RN26.1 -- every `STARTABLE` row carries its OWN checkbox too, DEFAULT CHECKED (unlike
 * `NEEDS_DECISION`, which defaults excluded): the mark is purely informational (`Manage preparation`,
 * the EXISTING key, zero i18n cost) and never removes a row from `Jobs to start` or from
 * `startableCount` -- it is the LEVER the operator uses to voluntarily shrink the confirmed batch,
 * never a reason the row is excluded by the server. 🔒 Why this matters (verified in this repo,
 * 2026-09-10): a job WITHOUT `managePreparation` whose open period is in preparation gets TRAPPED --
 * `common/reportOutcome.js` disables reporting by the period's STATUS CODE, while
 * `common/jobModeActions.js:59-90` (`:71-80`) decides the "Go to execution" exit by the job's
 * `managePreparation` SWITCH. A job that lands in preparation without that switch on falls into the
 * third branch (`:82`+): it can only be paused, never reported or moved to execution from the UI.
 * "Every job in preparation" was rejected during construction for exactly this reason -- the mark
 * lets the operator keep such jobs out of a batch that would otherwise start them in a mode the
 * interface cannot get them out of. La enmienda 06 lo mueve a una CELDA de la fila, sin cambiar nada
 * de eso.
 *
 * S125/POL4/RN18 -- `onConfirm` reports the EXPLICIT confirmed list (never a criterion the server
 * would have to resolve again) plus the raw `previewJobs` (so the caller can label N2's `started`/
 * `notStarted` -- which carry only ids -- without a second N1-shaped call).
 */
const rowKey = (row) => `${row?.fabricationOrderId}:${row?.jobReferenceId}`

const OUTCOME = {
  STARTABLE: 'STARTABLE',
  ALREADY_ACTIVE: 'ALREADY_ACTIVE',
  NEEDS_DECISION: 'NEEDS_DECISION',
  EXCLUDED: 'EXCLUDED'
}

/**
 * S234 -- desde cuántas filas de la respuesta se ofrece el buscador. El umbral se mide sobre la
 * respuesta ENTERA, no sobre una sección: lo que el PO no quiere es perderse en una tanda larga, y una
 * tanda larga puede estarlo por sus excluidos.
 */
export const PREVIEW_SEARCH_THRESHOLD = 15

/**
 * S233 -- la altura máxima y el scroll viven en la tabla de cada sección, para que el diálogo no crezca
 * con la cola. Desde la enmienda 07 el valor es el COMPARTIDO con el diálogo de cierre
 * (`batchTable.js`), que tiene la misma restricción por el mismo motivo; se sigue exportando con este
 * nombre porque es el que S233 nombra.
 */
export const PREVIEW_TABLE_CONTAINER_STYLE = BATCH_TABLE_CONTAINER_STYLE

/** S116/S236 -- las cinco columnas comunes a las cuatro secciones, con claves que YA existen en los 8. */
const BASE_COLUMN_LABELS = ['Fabrication order', 'Reference', 'Job', 'Group', 'Start date']

/**
 * S236/F1/CA2.2 -- el grupo se pinta VERBATIM (sin recortar ni normalizar) y solo se sustituye por
 * celda vacía cuando no hay nada que pintar: nulo, ausente o en blanco.
 */
const groupCellText = (value) => (typeof value === 'string' && value.trim() !== '' ? value : '')

/** S234 -- el filtro es del cliente y mira exactamente los tres campos que el escenario enumera. */
const matchesSearch = (row, needle) => {
  if (needle === '') return true
  const haystack = [row?.fabricationOrderCode, row?.partReferenceCode, row?.nodeName]
    .filter(Boolean).join(' ').toLowerCase()
  return haystack.includes(needle)
}

const PreviewSection = ({ titleKey, rows, visibleRows, t_, formatDateTime, headers = [], renderLeadingCell, renderTrailingCells }) => {
  if (rows.length === 0) return null
  return (
    <Stack spacing={1}>
      {/* S116/S235 -- el recuento es el TOTAL de la sección, nunca el de lo visible tras filtrar. */}
      <Typography variant='subtitle1' fontWeight='bold'>{`${t_(titleKey)} (${rows.length})`}</Typography>
      <TableContainer style={PREVIEW_TABLE_CONTAINER_STYLE}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              {renderLeadingCell && <TableCell padding='checkbox' />}
              {BASE_COLUMN_LABELS.map((label) => <TableCell key={label}>{t_(label)}</TableCell>)}
              {headers.map((label) => <TableCell key={label}>{t_(label)}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => (
              <TableRow key={rowKey(row)} hover>
                {renderLeadingCell && renderLeadingCell(row)}
                <TableCell>{row.fabricationOrderCode}</TableCell>
                <TableCell>{row.partReferenceCode}</TableCell>
                <TableCell>{row.nodeName}</TableCell>
                <TableCell>{groupCellText(row.planningGroup)}</TableCell>
                <TableCell>{formatDateTime(row.plannedStartDate)}</TableCell>
                {renderTrailingCells && renderTrailingCells(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

PreviewSection.propTypes = {
  titleKey: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  visibleRows: PropTypes.array.isRequired,
  t_: PropTypes.func.isRequired,
  formatDateTime: PropTypes.func.isRequired,
  headers: PropTypes.arrayOf(PropTypes.string),
  renderLeadingCell: PropTypes.func,
  renderTrailingCells: PropTypes.func
}

const BatchStartPreviewDialog = ({ open, request, onClose, onConfirm }) => {
  const { t_ } = useSmartTranslation()
  const { formatDateTime } = useDateFormatter()
  const requestRef = useRef(request)
  requestRef.current = request

  const [previewJobs, setPreviewJobs] = useState([])
  // Map rowKey -> boolean. STARTABLE defaults to included (absent/true), so `!== false` reads it;
  // NEEDS_DECISION defaults to excluded (absent/false), so `=== true` reads it (D-I7/S206).
  const [checkedStartable, setCheckedStartable] = useState({})
  const [includedNeedsDecision, setIncludedNeedsDecision] = useState({})
  const [searchText, setSearchText] = useState('')
  // Mismo agujero del doble clic que `useJobStatusChange`/`useJobModeTransition` (issue #2964): el
  // botón de aceptar emitía una tanda por clic. El bloqueo pesimista por job de Sun impide que se
  // dupliquen filas, pero no que se envíen peticiones repetidas. Va en ref y no en estado porque el
  // segundo clic del mismo lote de eventos leería el estado todavía sin actualizar. Una apertura,
  // una confirmación: se rearma abajo cuando el diálogo se vuelve a abrir.
  const confirmInFlightRef = useRef(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    confirmInFlightRef.current = false
    setPreviewJobs([])
    setCheckedStartable({})
    setIncludedNeedsDecision({})
    setSearchText('')
    previewBatchStart(requestRef.current).then((response) => {
      if (cancelled) return
      setPreviewJobs(Array.isArray(response?.jobs) ? response.jobs : [])
    })
    return () => { cancelled = true }
  }, [open])

  const startableRows = previewJobs.filter((row) => row?.outcome === OUTCOME.STARTABLE)
  const needsDecisionRows = previewJobs.filter((row) => row?.outcome === OUTCOME.NEEDS_DECISION)
  const excludedRows = previewJobs.filter((row) => row?.outcome === OUTCOME.EXCLUDED)
  const alreadyActiveRows = previewJobs.filter((row) => row?.outcome === OUTCOME.ALREADY_ACTIVE)

  // S234 -- el buscador se ofrece desde el umbral y filtra AQUÍ, sobre lo ya recibido: `previewJobs`
  // no se vuelve a pedir nunca, ni al escribir ni al borrar.
  const searchOffered = previewJobs.length >= PREVIEW_SEARCH_THRESHOLD
  const needle = searchText.trim().toLowerCase()
  const visible = (rows) => rows.filter((row) => matchesSearch(row, needle))

  const isStartableChecked = (row) => checkedStartable[rowKey(row)] !== false
  const toggleStartableChecked = (row) => {
    const key = rowKey(row)
    setCheckedStartable((prev) => ({ ...prev, [key]: !isStartableChecked(row) }))
  }

  const isNeedsDecisionIncluded = (row) => includedNeedsDecision[rowKey(row)] === true
  const toggleNeedsDecisionIncluded = (row) => {
    const key = rowKey(row)
    setIncludedNeedsDecision((prev) => ({ ...prev, [key]: !isNeedsDecisionIncluded(row) }))
  }

  // S235/RN18/POL4 -- se confirma lo MARCADO, sobre las filas SIN filtrar: el buscador ayuda a
  // encontrar, nunca cambia la tanda. Una fila que el filtro esconde sigue confirmándose.
  const confirmedJobs = [
    ...startableRows.filter(isStartableChecked),
    ...needsDecisionRows.filter(isNeedsDecisionIncluded)
  ].map((row) => ({
    fabricationOrderId: row.fabricationOrderId,
    jobReferenceId: row.jobReferenceId,
    managePreparation: row.managePreparation === true
  }))

  const noJobCanBeStarted = confirmedJobs.length === 0

  const rowLabel = (row) => [row.fabricationOrderCode, row.partReferenceCode, row.nodeName].filter(Boolean).join(' — ')

  const handleConfirm = () => {
    if (confirmInFlightRef.current) return
    confirmInFlightRef.current = true
    onConfirm({ confirmedJobs, previewJobs })
  }

  return (
    <MosSmartDialog
      title={createDialogTitle({ color: 'primary', title: t_('Start batch'), icon: PlaylistAddCheckIcon })}
      setOpen={open}
      cancelCallback={onClose}
      acceptCallback={handleConfirm}
      accept={!noJobCanBeStarted}
      maxPaperWidth='60rem'
    >
      <Stack spacing={3} sx={{ textAlign: 'left' }}>
        {noJobCanBeStarted && (
          <Typography color='warning.main' fontWeight='bold'>{t_('No job can be started')}</Typography>
        )}

        {searchOffered && (
          <TextField
            size='small'
            label={t_('Search')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        )}

        <PreviewSection
          titleKey='Jobs to start'
          rows={startableRows}
          visibleRows={visible(startableRows)}
          t_={t_}
          formatDateTime={formatDateTime}
          headers={['Manage preparation']}
          renderLeadingCell={(row) => (
            <TableCell padding='checkbox'>
              <Checkbox
                checked={isStartableChecked(row)}
                onChange={() => toggleStartableChecked(row)}
                inputProps={{ 'aria-label': rowLabel(row) }}
              />
            </TableCell>
          )}
          renderTrailingCells={(row) => (
            <TableCell>
              {row.managePreparation === true && (
                <Chip size='small' color='secondary' label={t_('Manage preparation')} />
              )}
            </TableCell>
          )}
        />

        <PreviewSection
          titleKey='Active on another machine'
          rows={needsDecisionRows}
          visibleRows={visible(needsDecisionRows)}
          t_={t_}
          formatDateTime={formatDateTime}
          headers={['Machine', 'Reason', 'Include']}
          renderTrailingCells={(row) => (
            <>
              <TableCell>
                {(Array.isArray(row.activeDevices) ? row.activeDevices : []).map((device) => device?.name).filter(Boolean).join(', ')}
              </TableCell>
              <TableCell><BatchReasonsList reasons={row.reasons} /></TableCell>
              <TableCell>
                <Button size='small' variant='outlined' onClick={() => toggleNeedsDecisionIncluded(row)}>
                  {isNeedsDecisionIncluded(row) ? t_('Exclude') : t_('Include')}
                </Button>
              </TableCell>
            </>
          )}
        />

        <PreviewSection
          titleKey='Jobs left out'
          rows={excludedRows}
          visibleRows={visible(excludedRows)}
          t_={t_}
          formatDateTime={formatDateTime}
          headers={['Reason']}
          renderTrailingCells={(row) => (
            <TableCell><BatchReasonsList reasons={row.reasons} /></TableCell>
          )}
        />

        {/* RN14 -- información, nunca error: la sección conserva su rótulo informativo. */}
        <PreviewSection
          titleKey='Already active on this machine'
          rows={alreadyActiveRows}
          visibleRows={visible(alreadyActiveRows)}
          t_={t_}
          formatDateTime={formatDateTime}
        />
      </Stack>
    </MosSmartDialog>
  )
}

BatchStartPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  request: PropTypes.shape({
    deviceId: PropTypes.number,
    workerId: PropTypes.number,
    selectionType: PropTypes.oneOf(['JOBS', 'PLANNING_GROUP', 'BATCH']),
    jobs: PropTypes.array,
    planningGroup: PropTypes.string,
    originJob: PropTypes.object,
    batchId: PropTypes.string
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
}

export default BatchStartPreviewDialog
